import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'

import styles from './StoryMapDeleteConfirm.module.css'

const StoryMapDeleteConfirm = ({
    open,
    title,
    message,
    detail,
    preview,
    previewIcon = 'mdi:delete-outline',
    confirmText = 'Delete',
    cancelText = 'Cancel',
    confirmingText = 'Deleting...',
    alertMode = false,
    centerActions = false,
    onCancel,
    onConfirm,
}) => {
    const [confirming, setConfirming] = useState(false)

    useEffect(() => {
        if (!open) {
            setConfirming(false)
        }
    }, [open])

    if (!open) return null

    const handleConfirm = async () => {
        if (confirming || !onConfirm) return

        if (alertMode) {
            onCancel?.()
            return
        }

        setConfirming(true)
        try {
            await onConfirm()
        } finally {
            setConfirming(false)
        }
    }

    const resolvedConfirmText = alertMode ? confirmText || 'OK' : confirmText
    const showAttachmentPreview = Boolean(preview)

    return createPortal(
        <div
            className={styles.overlay}
            data-story-map-confirm-overlay
            onClick={confirming ? undefined : onCancel}
            onMouseDown={(event) => event.stopPropagation()}
            role="presentation"
        >
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="story-map-delete-confirm-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.header}>
                    {showAttachmentPreview ? (
                        <span className={styles.preview} aria-hidden>
                            <img
                                src={preview}
                                alt=""
                                className={styles.previewImg}
                            />
                        </span>
                    ) : alertMode ? (
                        <span className={styles.alertIcon} aria-hidden>
                            <Icon icon={previewIcon} width={22} />
                        </span>
                    ) : (
                        <span className={styles.warningIcon} aria-hidden>
                            <Icon icon="mdi:alert-circle" width={24} />
                        </span>
                    )}
                    <p
                        id="story-map-delete-confirm-title"
                        className={styles.title}
                    >
                        {title}
                    </p>
                </div>

                {(message || detail) && (
                    <div className={styles.body}>
                        {message ? (
                            <p className={styles.message}>{message}</p>
                        ) : null}
                        {detail ? (
                            <p className={styles.detail} title={detail}>
                                {detail}
                            </p>
                        ) : null}
                    </div>
                )}

                <div
                    className={`${styles.actions} ${
                        centerActions ? styles.actionsCentered : ''
                    }`}
                >
                    {alertMode ? (
                        <button
                            type="button"
                            className={styles.alertOkBtn}
                            onClick={handleConfirm}
                        >
                            {resolvedConfirmText}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    onCancel?.()
                                }}
                                disabled={confirming}
                            >
                                {cancelText}
                            </button>
                            <button
                                type="button"
                                className={styles.confirmBtn}
                                onClick={handleConfirm}
                                disabled={confirming}
                            >
                                {confirming ? confirmingText : resolvedConfirmText}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}

export default StoryMapDeleteConfirm
